import React, {useState} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Stack,
    Typography
} from '@mui/material';
import QRCodeSVG from 'react-qr-code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import {useQuery} from 'react-query';
import {FormService} from 'crqlar-react-common/dist/services/forms/form.service';
import {Form} from 'crqlar-react-common/dist/models/forms/form';
import {useSnackbar} from 'notistack';
import {useTranslation} from 'react-i18next';

interface Props {
    profileId?: string;
    sectorId?: string;
    enabled?: boolean;
    qrCodeElementId?: string;
}

export default function GenerateLinkContent({
                                                 profileId,
                                                 sectorId,
                                                 enabled = true,
                                                 qrCodeElementId = 'qr-code-svg'
                                             }: Props) {
    const {enqueueSnackbar} = useSnackbar();
    const {t} = useTranslation();
    const [selectedForm, setSelectedForm] = useState<Form | null>(null);

    const formsQuery = useQuery({
        queryKey: ['forms-list', sectorId],
        queryFn: async () => {
            return await FormService.listForms({
                sectorId: sectorId ? [sectorId] : undefined,
                limit: {
                    offset: 0,
                    limit: 1000,
                    sort: [{field: 'title', direction: 'ASC'}]
                }
            });
        },
        enabled
    });

    const generateFormLink = (formId: string): string => {
        const base = `${window.location.origin}/public/form/${formId}`;
        return profileId ? `${base}?profileId=${profileId}` : base;
    };

    const handleCopyLink = () => {
        if (selectedForm) {
            navigator.clipboard.writeText(generateFormLink(selectedForm.id));
            enqueueSnackbar(t('Link copied to clipboard'), {variant: 'success'});
        }
    };

    const handleOpenLink = () => {
        if (selectedForm) {
            window.open(generateFormLink(selectedForm.id), '_blank');
        }
    };

    const handleDownloadQR = () => {
        if (!selectedForm) return;

        const svg = document.getElementById(qrCodeElementId);
        if (!svg) return;

        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;
            ctx?.drawImage(img, 0, 0);

            canvas.toBlob((blob) => {
                if (blob) {
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${selectedForm.title}-qr-code.png`;
                    link.click();
                    URL.revokeObjectURL(url);
                }
            });
        };

        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    };

    return (
        <Stack spacing={3} sx={{mt: 1}}>
            <Box>
                <Typography variant="subtitle1" gutterBottom>
                    {t('Select a form:')}
                </Typography>
                <Box
                    sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        maxHeight: 300,
                        overflow: 'auto'
                    }}
                >
                    {formsQuery.isLoading ? (
                        <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}>
                            <CircularProgress/>
                        </Box>
                    ) : formsQuery.data?.content && formsQuery.data.content.length > 0 ? (
                        <List disablePadding>
                            {formsQuery.data.content.map((form, index) => (
                                <React.Fragment key={form.id}>
                                    {index > 0 && <Divider/>}
                                    <ListItem disablePadding>
                                        <ListItemButton
                                            selected={selectedForm?.id === form.id}
                                            onClick={() => setSelectedForm(form)}
                                        >
                                            <ListItemText
                                                primary={form.title}
                                                secondary={form.description || t('No description')}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                </React.Fragment>
                            ))}
                        </List>
                    ) : (
                        <Box sx={{p: 4, textAlign: 'center'}}>
                            <Typography variant="body2" color="text.secondary">
                                {t('No forms available')}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {selectedForm && (
                <>
                    <Divider/>
                    <Box>
                        <Typography variant="subtitle1" gutterBottom>
                            {selectedForm.title}
                        </Typography>

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                p: 3,
                                backgroundColor: 'grey.50',
                                borderRadius: 1,
                                mb: 2
                            }}
                        >
                            <QRCodeSVG
                                id={qrCodeElementId}
                                value={generateFormLink(selectedForm.id)}
                                size={200}
                                level="H"
                            />
                        </Box>

                        <Box sx={{display: 'flex', justifyContent: 'center', mb: 2}}>
                            <Button
                                startIcon={<DownloadIcon/>}
                                variant="outlined"
                                size="small"
                                onClick={handleDownloadQR}
                            >
                                {t('Download QR Code')}
                            </Button>
                        </Box>

                        <Typography variant="caption" color="text.secondary" sx={{mb: 1, display: 'block'}}>
                            {t('Form Link:')}
                        </Typography>
                        <Box
                            sx={{
                                p: 2,
                                backgroundColor: 'grey.100',
                                borderRadius: 1,
                                wordBreak: 'break-all',
                                mb: 1
                            }}
                        >
                            <Typography variant="body2" sx={{fontFamily: 'monospace'}}>
                                {generateFormLink(selectedForm.id)}
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                            <Button
                                startIcon={<ContentCopyIcon/>}
                                variant="outlined"
                                size="small"
                                onClick={handleCopyLink}
                            >
                                {t('Copy Link')}
                            </Button>
                            <Button
                                startIcon={<OpenInNewIcon/>}
                                variant="outlined"
                                size="small"
                                onClick={handleOpenLink}
                            >
                                {t('Open Link')}
                            </Button>
                        </Stack>
                    </Box>
                </>
            )}
        </Stack>
    );
}
